(ns ballot-scout-v2-frontend.import-manager.events
  (:require
   [ajax.core :as ajax]
   [ajax.edn :refer [edn-request-format edn-response-format]]
   [ballot-scout-v2-frontend.api-urls :as urls]
   [ballot-scout-v2-frontend.db :as db]
   [ballot-scout-v2-frontend.events :as events]
   [ballot-scout-v2-frontend.import-manager.common :refer [default-search-state]]
   [ballot-scout-v2-frontend.request-helpers :as request-helpers]
   [ballot-scout-v2-frontend.subs :as subs]
   [ballot-scout-v2-frontend.util :as util]
   [clojure.set :as set]
   [clojure.string :as str]
   [day8.re-frame.http-fx]
   [goog.labs.format.csv :as csv]
   [re-frame.core :as rf]))

(def phase-mappings {"column-mapping" :import-phase/match-fields
                     "imb-generation" :import-phase/generate-imbs
                     "reissue-ballots" :import-phase/reissue-ballots
                     "ready-to-start" :import-phase/final
                     "importing-records" :import-phase/final
                     "generating-labels" :import-phase/final
                     "terminated-due-to-invalid-rows" :import-phase/invalid-rows
                     "completed" :import-phase/final})

(def ns-key :import-manager)

(rf/reg-event-fx
 ::fetch-import
 (fn [{:keys [db]} [_ import-id]]
   {:db (assoc-in db [ns-key :fetching-import :status] :fetching)
    ::events/ajax {:method :get
                   :uri (urls/import-by-id import-id)
                   :format (edn-request-format)
                   :response-format (edn-response-format)
                   :on-success [::fetch-import-success import-id]
                   :on-failure [::fetch-import-failure import-id]}}))

(rf/reg-event-fx
 ::fetch-import-success
 (fn [{:keys [db]} [_ import-id response]]
   {:db (-> db
            (db/add-entity response)
            (assoc-in [ns-key :fetching-import :status] :success))
    :dispatch [::request-helpers/clear-failed-request [::fetch-import import-id]]}))

(rf/reg-event-fx
 ::fetch-import-failure
 (fn [{:keys [db]} [_ import-id response]]
   {:db (assoc-in db [ns-key :fetching-import :status] :errored)
    :dispatch [::request-helpers/request-failed [::fetch-import import-id] response]}))

(rf/reg-event-db
 ::clear
 (fn [db [_]]
   (dissoc db ns-key)))

(rf/reg-event-fx
 ::upload
 (fn [{:keys [db]} [_ jurisdiction-id election-slug blob]]
   {:db (assoc-in db [ns-key :upload] {:status :uploading})
    ::events/ajax {:method :post
                   :body (doto (js/FormData.) (.append "file" blob))
                   :uri (urls/new-import-upload jurisdiction-id election-slug)
                   :format (ajax/url-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::upload-success jurisdiction-id election-slug (hash blob)]
                   :on-failure [::upload-failure jurisdiction-id election-slug (hash blob)]}}))

(rf/reg-event-fx
 ::upload-success
 (fn [{:keys [db]} [_ jurisdiction-id election-slug blob-hash response]]
   {:db (db/add-entity db response)
    :dispatch-n [[::request-helpers/clear-failed-request
                  [::upload jurisdiction-id election-slug blob-hash]]
                 [:ballot-scout-v2-frontend.routes/navigate
                  :ballot-scout-v2-frontend.routes/import-manager
                  {:partner-slug @(rf/subscribe [::subs/current-partner-slug])
                   :jurisdiction-slug @(rf/subscribe [::subs/current-jurisdiction-slug])
                   :election-slug @(rf/subscribe [::subs/current-election-slug])
                   :import-id (:import/id response)}]]}))

(rf/reg-event-fx
 ::upload-failure
 (fn [{:keys [db]} [_ jurisdiction-id election-slug blob-hash response]]
   {:db (-> db
            (assoc-in [ns-key :upload :status] :errored)
            (assoc-in [ns-key :upload :error-response] (:status-text response)))
    :dispatch [::request-helpers/request-failed
               [::upload jurisdiction-id election-slug blob-hash] response]}))

(rf/reg-event-db
 ::set-current-phase
 (fn [db [_ import-id phase]]
   (assoc-in db [ns-key :data import-id :phase] phase)))

(defn assoc-header-state [db import-id state]
  (update-in db [ns-key :data import-id :header-state] merge state))

(defn parse-csv-line [s]
  (first (csv/parse s)))

(rf/reg-event-db
 ::set-headers
 (fn [db [_ import-id headers]]
   (assoc-header-state db import-id {:status :fetched :headers headers})))

(rf/reg-event-fx
 ::fetch-headers
 (fn [{:keys [db]} [_ {:import/keys [id original-file-url]}]]
   {:db (assoc-header-state db id {:status :fetching})
    ::events/fetch-lines {:url original-file-url
                          :limit 1
                          :on-line #(rf/dispatch [::set-headers id (parse-csv-line %)])}}))

(rf/reg-event-fx
 ::post-column-mapping
 (fn [{:keys [db]} [_ {:import/keys [id] :as import} column-mapping]]
   {:db (assoc-header-state db id {:status :setting-column-mapping})
    :dispatch [::poll-for-current-import #(not= "column-mapping" (:import/phase %))]
    ::events/ajax {:method :post
                   :params (->> column-mapping
                                (remove (fn [[_ v]] (string? v)))
                                (into {})
                                set/map-invert)
                   :uri (urls/set-import-column-mapping id)
                   :format (ajax.edn/edn-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::post-column-mapping-success import column-mapping]
                   :on-failure [::post-column-mapping-failure import column-mapping]}}))

(rf/reg-event-fx
 ::post-column-mapping-success
 (fn [{:keys [db]} [_ import column-mapping response]]
   {:db (db/add-entity db response)
    :dispatch [::request-helpers/clear-failed-request [::post-column-mapping import column-mapping]]
    :interval {:id ::poll-import :action :stop}}))

(rf/reg-event-fx
 ::post-column-mapping-failure
 (fn [{:keys [db]} [_ import column-mapping response]]
   {:db (-> db
            (assoc-in [ns-key :import-phase/match-fields :status]
                      :errored)
            (assoc-in [ns-key :import-phase/match-fields :error-response]
                      (:status-text response)))
    :dispatch [::request-helpers/request-failed [::post-column-mapping import column-mapping] response]
    :interval {:id ::poll-import :action :stop}}))

(rf/reg-event-fx
 ::post-imb-generation
 (fn [{:keys [db]}
      [_
       {:import/keys [id] :as import}
       {:keys [mailer-config-id generate-vb-imbs? generate-ob-imbs? generate-labels?] :as opts}]]
   {:db (assoc-in db [ns-key :data id :imb-generation-state :status] :setting)
    ::events/ajax {:method :post
                   :params {:mailer-config-id mailer-config-id
                            :should-generate-vb-imbs generate-vb-imbs?
                            :should-generate-ob-imbs generate-ob-imbs?
                            :should-generate-labels generate-labels?}
                   :uri (urls/set-imb-generation id)
                   :format (ajax.edn/edn-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::post-imb-generation-success import opts]
                   :on-failure [::post-imb-generation-failure import opts]}}))

(rf/reg-event-fx
 ::post-imb-generation-success
 (fn [{:keys [db]} [_ import opts response]]
   {:db (db/add-entity db response)
    :dispatch [::request-helpers/clear-failed-request [::post-imb-generation import opts]]}))

(rf/reg-event-fx
 ::post-imb-generation-failure
 (fn [{:keys [db]} [_ import opts response]]
   {:db (-> db
            (assoc-in [ns-key :import-phase/reissue-ballots :status] :errored)
            (assoc-in [ns-key :import-phase/reissue-ballots :error-response] (:status-text response)))
    :dispatch [::request-helpers/request-failed [::post-imb-generation import opts] response]}))

(rf/reg-event-db
 ::set-existing-voters-status
 (fn [db [_ import-id status]]
   (assoc-in db [ns-key :data import-id :existing-voters-state :status] status)))

(rf/reg-event-db
 ::append-existing-voter-row
 (fn [db [_ import-id row]]
   (let [header-row (get-in db [ns-key :data import-id :existing-voters-state :header-row])]
     (if (nil? header-row)
       (assoc-in db [ns-key :data import-id :existing-voters-state :header-row] row)
       (update-in db
                  [ns-key :data import-id :existing-voters-state :rows]
                  #(vec (conj % (zipmap header-row row))))))))

(rf/reg-event-fx
 ::fetch-existing-voter-rows
 (fn [{:keys [db]} [_ {:import/keys [id existing-voters-file-url]}]]
   {:db (assoc-in db [ns-key :data id :existing-voters-state :status] :fetching)
    ::events/fetch-lines {:url existing-voters-file-url
                          :on-line #(rf/dispatch [::append-existing-voter-row id (parse-csv-line %)])
                          :on-complete #(rf/dispatch [::set-existing-voters-status id :complete])}}))

(rf/reg-event-db
 ::set-invalid-rows-status
 (fn [db [_ import-id status]]
   (assoc-in db [ns-key :data import-id :invalid-rows-state :status] status)))

(rf/reg-event-db
 ::append-invalid-row
 (fn [db [_ import-id row]]
   (let [header-row (get-in db [ns-key :data import-id :invalid-rows-state :header-row])]
     (if (nil? header-row)
       (assoc-in db [ns-key :data import-id :invalid-rows-state :header-row] row)
       (update-in db
                  [ns-key :data import-id :invalid-rows-state :rows]
                  #(vec (conj % (zipmap header-row row))))))))

(rf/reg-event-fx
 ::fetch-invalid-rows
 (fn [{:keys [db]} [_ {:import/keys [id invalid-rows-file-url]}]]
   {:db (assoc-in db [ns-key :data id :invalid-rows-state :status] :fetching)
    ::events/fetch-lines {:url invalid-rows-file-url
                          :on-line #(rf/dispatch [::append-invalid-row id (str/split % \,)])
                          :on-complete #(rf/dispatch [::set-invalid-rows-status id :complete])}}))

(rf/reg-event-fx
 ::post-reissue-ballot-config
 (fn [{:keys [db]}
      [_
       {:import/keys [id] :as import}
       {:keys [should-reissue-all-ballots? voters-to-reissue] :as opts}]]
   {:db (assoc-in db [ns-key :data id :existing-voters-state :status] :setting-reissue-ballot-config)
    ::events/ajax {:method :post
                   :params {:should-reissue-all-ballots should-reissue-all-ballots?
                            :voters-to-reissue (not-empty (vec voters-to-reissue))}
                   :uri (urls/set-reissue-ballot-config id)
                   :format (ajax.edn/edn-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::post-reissue-ballot-config-success import opts]
                   :on-failure [::post-reissue-ballot-config-failure import opts]}}))

(rf/reg-event-fx
 ::post-reissue-ballot-config-success
 (fn [{:keys [db]} [_ import opts response]]
   {:db (db/add-entity db response)
    :dispatch [::request-helpers/clear-failed-request [::post-reissue-ballot-config import opts]]}))

(rf/reg-event-fx
 ::post-reissue-ballot-config-failure
 (fn [{:keys [db]} [_ import opts response]]
   {:db (-> db
            (assoc-in [ns-key :import-phase/reissue-ballots :status] :errored)
            (assoc-in [ns-key :import-phase/reissue-ballots :error-response] (:status-text response)))
    :dispatch [::request-helpers/request-failed [::post-reissue-ballot-config import opts] response]}))

(rf/reg-event-fx
 ::start-import
 (fn [{:keys [db]} [_ {:import/keys [id] :as import}]]
   {:db (assoc-in db [ns-key :data id :ready-state :status] :starting)
    :dispatch [::poll-for-completed-import]
    ::events/ajax {:method :post
                   :uri (urls/start-import id)
                   :format (ajax.edn/edn-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::start-import-success import]
                   :on-failure [::start-import-failure import]}}))

(rf/reg-event-fx
 ::start-import-success
 (fn [{:keys [db]} [_ import response]]
   {:db (db/add-entity db response)
    :dispatch [::request-helpers/clear-failed-request [::start-import import]]}))

(rf/reg-event-fx
 ::start-import-failure
 (fn [{:keys [db]} [_ import response]]
   {:db (-> db
            (assoc-in [ns-key :import-phase/reissue-ballots :status] :errored)
            (assoc-in [ns-key :import-phase/reissue-ballots :error-response] (:status-text response)))
    :dispatch [::request-helpers/request-failed [::start-import import] response]}))

(rf/reg-event-fx
 ::fetch-imports-list-for-route
 (fn [{:keys [db]} [_ route]]
   (let [query-params (merge default-search-state (:query-params route))]
     {:db (assoc-in db [ns-key :imports-list-state :status] :fetching)
      ::events/ajax {:method :get
                     :uri (urls/imports-list (get-in route [:path-params :jurisdiction-slug])
                                             (get-in route [:path-params :election-slug]))
                     :params query-params
                     :format (ajax.edn/edn-request-format)
                     :response-format (ajax.edn/edn-response-format)
                     :on-success [::fetch-imports-list-success route]
                     :on-failure [::fetch-imports-list-failure route]}})))

(rf/reg-event-fx
 ::fetch-imports-list-success
 (fn [{:keys [db]} [_ route response]]
   {:db (-> db
            (assoc-in [ns-key :imports-list-state :status] :success)
            (assoc-in [ns-key :imports-list-state :data] response))
    :dispatch [::request-helpers/clear-failed-request [::fetch-imports-list-for-route route]]}))

(rf/reg-event-fx
 ::fetch-imports-list-failure
 (fn [{:keys [db]} [_ route response]]
   {:db (-> db
            (assoc-in [ns-key :imports-list-state :status] :errored)
            (assoc-in [ns-key :imports-list-state :error-response] (:status-text response)))
    :dispatch [::request-helpers/request-failed [::fetch-imports-list-for-route route] response]}))

(rf/reg-event-db
 ::clear-import-state
 (fn [db _]
   (assoc-in db [ns-key :imports-list-state] nil)))

(rf/reg-event-fx
 ::poll-for-import-query
 (fn [_ [_ import-id stop-fn]]
   {::events/ajax {:method :get
                   :uri (urls/import-by-id import-id)
                   :format (ajax.edn/edn-request-format)
                   :response-format (ajax.edn/edn-response-format)
                   :on-success [::poll-for-import-query-success import-id stop-fn]
                   :on-failure [::poll-for-import-query-failure import-id stop-fn]}}))

(rf/reg-event-fx
 ::poll-for-import-query-success
 (fn [{:keys [db]} [_ import-id stop-fn import-response]]
   (merge
    {:db (db/add-entity db import-response)
     :dispatch [::request-helpers/clear-failed-request [::poll-for-import-query import-id stop-fn]]}
    (when (stop-fn import-response)
      {:interval {:id ::poll-import :action :stop}}))))

(rf/reg-event-fx
 ::poll-for-import-query-failure
 (fn [_ import-id stop-fn response]
   {:interval {:id ::poll-import :action :stop}
    :dispatch [::request-helpers/request-failed [::poll-for-import-query import-id stop-fn] response]}))

(rf/reg-event-fx
 ::poll-for-current-import
 (fn [{:keys [db]} [_ stop-fn]]
   (when-let [current-import-id (-> db
                                    :current-route
                                    (get-in [:path-params :import-id])
                                    util/->int)]
     {:interval {:dispatch [::poll-for-import-query current-import-id stop-fn]
                 :action :start
                 :ms 5000
                 :id ::poll-import}})))

(rf/reg-event-fx
 ::poll-for-completed-import
 (fn [_ _]
   {:dispatch [::poll-for-current-import #(= "completed" (:import/phase %))]}))
